<template>
  <div class="success-page">
    <div class="container">

      <Spinner v-if="loading" message="Hang tight — we're getting your postcard ready!" />

      <div v-if="showModal" class="modal">
        <div class="modal-content">
          <p>{{ modalMessage }}</p>
          <button @click="closeModal" class="button">Close</button>
        </div>
      </div>

      <!-- Success State -->
      <div class="success-section">
        <div class="success-icon">
          <font-awesome-icon :icon="['fas', 'check']" />
        </div>

        <h1>Postcard on its way!</h1>
        <p class="success-message">
          Your personalized postcard is being created and will be mailed soon.
        </p>

        <div class="delivery-estimate">
          <font-awesome-icon :icon="['fas', 'paper-plane']" />
          <span>Postcards typically arrive in <strong>5-7 business days</strong></span>
        </div>

        <div class="info-section">
          <h2>What Happens Next?</h2>
          <div class="steps-grid">
            <div class="step-item">
              <div class="step-number">1</div>
              <p>Your unique image and message are created</p>
            </div>
            <div class="step-item">
              <div class="step-number">2</div>
              <p>Your postcard is printed on premium cardstock</p>
            </div>
            <div class="step-item">
              <div class="step-number">3</div>
              <p>It's mailed to surprise your recipient</p>
            </div>
          </div>
        </div>

        <!-- Share Section -->
        <div class="share-section">
          <p class="share-prompt">Know someone who'd love this?</p>
          <div class="share-buttons">
            <button @click="shareOnTwitter" class="share-btn share-twitter" title="Share on X/Twitter">
              <font-awesome-icon :icon="['fab', 'x-twitter']" />
            </button>
            <button @click="shareOnFacebook" class="share-btn share-facebook" title="Share on Facebook">
              <font-awesome-icon :icon="['fab', 'facebook-f']" />
            </button>
            <button @click="copyShareLink" class="share-btn share-copy" :title="linkCopied ? 'Copied!' : 'Copy link'">
              <font-awesome-icon :icon="linkCopied ? ['fas', 'check'] : ['fas', 'link']" />
            </button>
            <button v-if="canNativeShare" @click="nativeShare" class="share-btn share-native" title="Share">
              <font-awesome-icon :icon="['fas', 'share-alt']" />
            </button>
          </div>
        </div>

        <div class="success-buttons">
          <router-link to="/create-postcard" class="btn btn-primary">
            <font-awesome-icon :icon="['fas', 'pen']" />
            Create Another Postcard
          </router-link>
        </div>

        <div class="thank-you">
          <font-awesome-icon :icon="['fas', 'heart']" class="heart-icon" />
          <p>Thank you for spreading joy with <strong>Thinking of U</strong></p>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {mapActions, mapGetters} from "vuex";
import {getAuth, signInAnonymously} from 'firebase/auth';
import {verifyCheckoutSession, addPostcardsToQueue} from '@/api';
import {track, USD, POSTCARD_ITEM} from '@/utils/track';
import Spinner from "@/components/SpinnerComp.vue";

export default {
  name: 'SuccessPage',
  computed: {
    ...mapGetters(['cartItems']),
    canNativeShare() {
      return typeof navigator !== 'undefined' && navigator.share;
    },
    shareText() {
      return "I just sent a one-of-a-kind postcard to surprise someone special! Check out Thinking of U - the easiest way to brighten someone's day.";
    },
    shareUrl() {
      return window.location.origin;
    },
  },
  components: {
    Spinner,
  },
  data() {
    return {
      loading: false,
      showModal: false,
      modalMessage: '',
      alreadyProcessed: false,
      linkCopied: false,
    };
  },
  async mounted() {
    const sessionId = this.$route.query.session_id;

    // Check if this session was already processed
    if (sessionId && this.isSessionProcessed(sessionId)) {
      this.alreadyProcessed = true;
      return;
    }

    // No session_id and no cart items - user navigated here directly
    if (!sessionId && this.cartItems.length === 0) {
      return;
    }

    // Have session_id but no cart items - localStorage may have been cleared
    if (sessionId && this.cartItems.length === 0) {
      this.modalMessage = 'Your order may have already been processed. If you believe this is an error, please contact support.';
      this.showModal = true;
      return;
    }

    // Normal flow: have cart items and session_id
    if (this.cartItems.length > 0) {
      this.loading = true;
      if (!sessionId) {
        this.modalMessage = 'Invalid session. Please try again or contact support.';
        this.showModal = true;
        this.loading = false;
        return;
      }
      await this.handlePostSuccessFlow(sessionId);
      this.loading = false;
    }
  },
  methods: {
    ...mapActions(['clearPostcard', 'clearCart']),
    isSessionProcessed(sessionId) {
      try {
        const processed = JSON.parse(sessionStorage.getItem('processedSessions') || '[]');
        return processed.includes(sessionId);
      } catch {
        return false;
      }
    },
    markSessionProcessed(sessionId) {
      try {
        const processed = JSON.parse(sessionStorage.getItem('processedSessions') || '[]');
        if (!processed.includes(sessionId)) {
          processed.push(sessionId);
          sessionStorage.setItem('processedSessions', JSON.stringify(processed));
        }
      } catch {
        sessionStorage.setItem('processedSessions', JSON.stringify([sessionId]));
      }
    },
    async handlePostSuccessFlow(sessionId) {
      const auth = getAuth();

      if (!auth.currentUser) {
        await signInAnonymously(auth);
      }

      if (!Array.isArray(this.cartItems) || this.cartItems.length === 0) {
        this.modalMessage = 'No postcards to process. If you believe this is an error, please contact support.';
        this.showModal = true;
        return;
      }

      let proceed = false;
      try {
        const {data} = await verifyCheckoutSession({sessionId});
        proceed = data.success;
      } catch (err) {
        this.modalMessage = 'Something went wrong. Please contact support.';
        this.showModal = true;
        proceed = false;
      }

      if (proceed) {
        try {
          const testModeKey = sessionStorage.getItem('testModeKey');
          await addPostcardsToQueue({
            postcards: this.cartItems,
            sessionId: sessionId,
            ...(testModeKey && {testModeKey}),
          });
        } catch (err) {
          this.modalMessage = 'Something went wrong. Please contact support.';
          this.showModal = true;
          return;
        }

        // Track the completed purchase before the cart is cleared. This block only
        // runs once per session (isSessionProcessed short-circuits refreshes) and
        // transaction_id lets GA4 dedupe as a final guard.
        track('purchase', {
          transaction_id: sessionId,
          currency: USD,
          value: this.cartItems.reduce((total, item) => total + (item.price || 0), 0),
          // price is the per-unit price so items[] revenue (price * quantity)
          // reconciles with the top-level value above.
          items: [{ ...POSTCARD_ITEM, price: this.cartItems[0]?.price || 0, quantity: this.cartItems.length }],
        });

        // Mark this session as processed to prevent duplicate processing on refresh
        this.markSessionProcessed(sessionId);
        this.clearPostcard();
        this.clearCart();
      }
    },
    closeModal() {
      this.showModal = false;
    },
    shareOnTwitter() {
      const text = encodeURIComponent(this.shareText);
      const url = encodeURIComponent(this.shareUrl);
      window.open(
        `https://twitter.com/intent/tweet?text=${text}&url=${url}`,
        '_blank',
        'width=550,height=420'
      );
    },
    shareOnFacebook() {
      const url = encodeURIComponent(this.shareUrl);
      window.open(
        `https://www.facebook.com/sharer/sharer.php?u=${url}`,
        '_blank',
        'width=550,height=420'
      );
    },
    async copyShareLink() {
      try {
        await navigator.clipboard.writeText(`${this.shareText} ${this.shareUrl}`);
        this.linkCopied = true;
        this.$toast.success('Link copied to clipboard!');
        setTimeout(() => {
          this.linkCopied = false;
        }, 2000);
      } catch (err) {
        this.$toast.error('Failed to copy link');
      }
    },
    async nativeShare() {
      try {
        await navigator.share({
          title: 'Thinking of U',
          text: this.shareText,
          url: this.shareUrl,
        });
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Share failed:', err);
        }
      }
    },
  }
}
</script>

<style scoped>
.success-page {
  padding: 60px 20px;
  min-height: 100vh;
  background-color: var(--light-pink);
  font-family: 'Inter', sans-serif;
}

.container {
  max-width: 720px;
  margin: 0 auto;
  background: white;
  padding: 48px 40px;
  border-radius: 16px;
  box-shadow: 0 6px 20px rgba(0, 0, 0, 0.06);
}

/* Success Section */
.success-section {
  text-align: center;
}

.success-icon {
  width: 5rem;
  height: 5rem;
  border-radius: 50%;
  background: var(--hollywood-cerise);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.25rem;
  margin: 0 auto 1.5rem;
  box-shadow: 0 4px 12px rgba(230, 0, 126, 0.3);
  animation: checkBounce 0.6s cubic-bezier(0.34, 1.56, 0.64, 1) both;
}

@keyframes checkBounce {
  from {
    opacity: 0;
    transform: scale(0.3);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
}

h1 {
  font-size: 2rem;
  color: var(--hollywood-cerise);
  margin-bottom: 0.5rem;
  animation: fadeInUp 0.5s ease 0.2s both;
}

.success-message {
  color: #64748b;
  font-size: 1.1rem;
  margin-bottom: 1.5rem;
  animation: fadeInUp 0.5s ease 0.3s both;
}

.delivery-estimate {
  display: inline-flex;
  align-items: center;
  gap: 0.75rem;
  background: var(--light-pink);
  border: 1px solid var(--powder-blue);
  border-radius: 8px;
  padding: 1rem 1.5rem;
  margin-bottom: 2rem;
  color: var(--hollywood-cerise);
  animation: fadeInUp 0.5s ease 0.4s both;
}

.delivery-estimate svg {
  color: var(--hollywood-cerise);
}

/* Info Section with Steps */
.info-section {
  margin: 2rem 0;
  padding: 1.5rem;
  background: #fafafa;
  border-radius: 12px;
  animation: fadeInUp 0.5s ease 0.5s both;
}

.info-section h2 {
  font-size: 1.25rem;
  color: var(--hollywood-cerise);
  margin-bottom: 1.5rem;
}

.steps-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1rem;
}

.step-item {
  text-align: center;
}

.step-number {
  width: 2rem;
  height: 2rem;
  border-radius: 50%;
  background: var(--hollywood-cerise);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  margin: 0 auto 0.75rem;
  font-size: 0.9rem;
}

.step-item p {
  font-size: 0.9rem;
  color: var(--rich-black);
  line-height: 1.4;
}

/* Share Section */
.share-section {
  margin: 2rem 0;
  padding: 1.5rem;
  background: var(--light-pink);
  border-radius: 12px;
  animation: fadeInUp 0.5s ease 0.6s both;
}

@keyframes fadeInUp {
  from {
    opacity: 0;
    transform: translateY(15px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.share-prompt {
  color: var(--hollywood-cerise);
  font-size: 1rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.share-buttons {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
}

.share-btn {
  width: 3rem;
  height: 3rem;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.25rem;
  transition: all 0.2s;
}

.share-btn:hover {
  transform: translateY(-2px) scale(1.1);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
}

.share-twitter {
  background: #000;
  color: white;
}

.share-twitter:hover {
  background: #1a1a1a;
}

.share-facebook {
  background: #1877f2;
  color: white;
}

.share-facebook:hover {
  background: #0d65d9;
}

.share-copy {
  background: var(--violet-lush);
  color: white;
}

.share-copy:hover {
  background: var(--hollywood-cerise);
}

.share-native {
  background: var(--hollywood-cerise);
  color: white;
}

.share-native:hover {
  background: var(--violet-lush);
}

/* Success Buttons */
.success-buttons {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  margin: 2rem 0;
}

.btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 14px 28px;
  border-radius: 999px;
  font-weight: 600;
  font-size: 1rem;
  text-decoration: none;
  transition: all 0.2s;
  min-width: 240px;
}

.btn-primary {
  background: var(--hollywood-cerise);
  color: white;
  border: none;
}

.btn-primary:hover {
  background: var(--violet-lush);
}

.btn-secondary {
  background: transparent;
  color: var(--hollywood-cerise);
  border: 2px solid var(--hollywood-cerise);
}

.btn-secondary:hover {
  background: var(--hollywood-cerise);
  color: white;
}

/* Thank You */
.thank-you {
  margin-top: 2rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eee;
}

.heart-icon {
  color: var(--hollywood-cerise);
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.thank-you p {
  color: #64748b;
  font-size: 0.95rem;
}

/* Modal */
.modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1001;
}

.modal-content {
  background-color: white;
  padding: 24px;
  border-radius: 12px;
  text-align: center;
  max-width: 400px;
  margin: 0 20px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
}

.modal-content p {
  color: var(--rich-black);
  margin-bottom: 1rem;
}

.modal-content .button {
  background: var(--hollywood-cerise);
  color: white;
  border: none;
  padding: 10px 24px;
  border-radius: 999px;
  cursor: pointer;
  font-weight: 600;
}

/* Mobile */
@media (max-width: 600px) {
  .container {
    padding: 24px 20px;
    margin: 0 10px;
  }

  h1 {
    font-size: 1.6rem;
  }

  .success-icon {
    width: 4rem;
    height: 4rem;
    font-size: 1.75rem;
  }

  .steps-grid {
    grid-template-columns: 1fr;
    gap: 1.25rem;
  }

  .step-item {
    display: flex;
    align-items: center;
    gap: 1rem;
    text-align: left;
  }

  .step-number {
    margin: 0;
    flex-shrink: 0;
  }

  .btn {
    width: 100%;
    min-width: unset;
  }

  .delivery-estimate {
    display: flex;
    flex-direction: column;
    text-align: center;
    gap: 0.5rem;
  }
}
</style>
